import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Modal, Card, Badge, Row, Col, Alert } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import homepageApi from '../../api/homepage';
import CustomInput from '../../common/FormComponents/Input';
import DeleteConfirmationModal from '../../common/UI/CustomModal/deleteModal';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import { getFormDataFromObject } from '../../utils/helpers';
import ReactSwitch from 'react-switch';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

const validationSchema = Yup.object({
  number: Yup.number().required('Number is required').min(0, 'Number must be positive').max(100, 'Number cannot exceed 100'),
  text: Yup.string().required('Text is required'),
});

const AnalyticsManager = ({ section, backToList, refreshSections }) => {
  const [items, setItems] = useState(section.items || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await homepageApi.deleteItemFromSection(section.sectionKey, deleteItemId);
      toastSuccess('Analytics stat deleted successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to delete analytics stat');
      console.error(error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await homepageApi.getHomepageSection(section.sectionKey);
      setItems(response?.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleStatus = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({ status: !currentStatus });
      await homepageApi.updateItemInSection(section.sectionKey, itemId, payload);
      toastSuccess('Status updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({
        number: values.number,
        text: values.text,
        status: values.status !== undefined ? values.status : true,
      });

      if (editingItem) {
        await homepageApi.updateItemInSection(section.sectionKey, editingItem._id, payload);
        toastSuccess('Analytics stat updated successfully');
      } else {
        await homepageApi.addItemToSection(section.sectionKey, payload);
        toastSuccess('Analytics stat added successfully');
      }

      refreshSections();
      fetchItems();
      setShowModal(false);
      resetForm();
      setEditingItem(null);
    } catch (error) {
      toastError(editingItem ? 'Failed to update analytics stat' : 'Failed to add analytics stat');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(sortedItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    const updatedItems = reorderedItems.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    setItems(reorderedItems.map((item, index) => ({ ...item, order: index })));

    try {
      await homepageApi.reorderItems(section.sectionKey, { items: updatedItems });
      toastSuccess('Order updated successfully');
    } catch (error) {
      toastError('Failed to update order');
      fetchItems();
    }
  };

  if (loading && !items.length) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                <i className="fa fa-home me-2"></i>Landing Page CMS
              </li>
              <li className="active">Analytics Management</li>
            </ol>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              {/* Header */}
              <div className="header_top border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className="mb-2">
                      <i className="fa fa-chart-line me-2 text-primary"></i>
                      Analytics & Stats Management
                    </h3>
                    <p className="text-muted mb-2">
                      Showcase your business metrics and achievements to build credibility
                    </p>
                    <div className="d-flex gap-2 align-items-center">
                      <Badge bg="primary" className="text-uppercase">List Section</Badge>
                      <Badge bg="secondary" className="font-monospace">{section.sectionKey}</Badge>
                      <Badge bg="info">{sortedItems.length} Stats</Badge>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button variant="success" onClick={handleAdd} disabled={loading}>
                      <i className="fa fa-plus me-2"></i>Add New Stat
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={backToList}>
                      <i className="fa fa-arrow-left me-2"></i>Back
                    </Button>
                  </div>
                </div>
              </div>

              <div className="middle_main pt-4">
                {sortedItems.length === 0 ? (
                  <Card className="text-center py-5 border-0 bg-light">
                    <Card.Body>
                      <i className="fa fa-chart-bar fa-5x text-muted mb-4 d-block"></i>
                      <h4 className="text-muted mb-2">No Analytics Stats Yet</h4>
                      <p className="text-muted mb-4">
                        Add powerful statistics to showcase your business success
                      </p>
                      <Button variant="primary" size="lg" onClick={handleAdd}>
                        <i className="fa fa-plus me-2"></i>Add Your First Stat
                      </Button>
                    </Card.Body>
                  </Card>
                ) : (
                  <>
                    <Alert variant="info" className="mb-4">
                      <i className="fa fa-info-circle me-2"></i>
                      <strong>Tip:</strong> Drag cards to reorder stats. Enter percentage numbers (0-100) that highlight your business success!
                    </Alert>

                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="analytics">
                        {(provided, snapshot) => (
                          <Row
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{
                              backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : 'transparent',
                              padding: '10px',
                              borderRadius: '8px',
                              transition: 'background-color 0.2s ease',
                            }}
                          >
                            {sortedItems.map((item, index) => (
                              <Draggable key={item._id} draggableId={item._id} index={index}>
                                {(provided, snapshot) => (
                                  <Col
                                    lg={4}
                                    md={6}
                                    sm={12}
                                    className="mb-4"
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                  >
                                    <Card
                                      className="h-100 shadow-sm border-0"
                                      style={{
                                        backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'white',
                                        transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                                        transition: 'all 0.2s ease',
                                        borderLeft: '4px solid #0d6efd',
                                      }}
                                    >
                                      {/* Card Header with Drag Handle */}
                                      <Card.Header className="bg-gradient border-0" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <div
                                              {...provided.dragHandleProps}
                                              style={{ cursor: 'grab', color: 'white' }}
                                            >
                                              <i className="fa fa-grip-vertical"></i>
                                            </div>
                                            <Badge bg="light" text="dark" pill>
                                              #{index + 1}
                                            </Badge>
                                          </div>
                                          <ReactSwitch
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            onChange={() => handleToggleStatus(item._id, item.status)}
                                            checked={item.status}
                                            height={20}
                                            width={40}
                                            onColor="#28a745"
                                            offColor="#6c757d"
                                          />
                                        </div>
                                      </Card.Header>

                                      {/* Card Body with Stats */}
                                      <Card.Body className="text-center py-4">
                                        <div className="mb-3">
                                          <h1 className="display-4 fw-bold text-primary mb-0">
                                            {item.number}%
                                          </h1>
                                        </div>
                                        <p className="text-muted mb-0">
                                          {item.text}
                                        </p>
                                      </Card.Body>

                                      {/* Card Footer with Actions */}
                                      <Card.Footer className="bg-light border-0">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div>
                                            {item.status ? (
                                              <Badge bg="success">
                                                <i className="fa fa-eye me-1"></i>Visible
                                              </Badge>
                                            ) : (
                                              <Badge bg="secondary">
                                                <i className="fa fa-eye-slash me-1"></i>Hidden
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="d-flex gap-2">
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              onClick={() => handleEdit(item)}
                                            >
                                              <i className="fa fa-edit"></i>
                                            </Button>
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() => handleDelete(item._id)}
                                            >
                                              <i className="fa fa-trash"></i>
                                            </Button>
                                          </div>
                                        </div>
                                      </Card.Footer>
                                    </Card>
                                  </Col>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Row>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-gradient text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <Modal.Title>
            <i className={`fa fa-${editingItem ? 'edit' : 'plus'} me-2`}></i>
            {editingItem ? 'Edit' : 'Add'} Analytics Stat
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            number: editingItem?.number || '',
            text: editingItem?.text || '',
            status: editingItem ? editingItem.status : true,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form>
              <Modal.Body>
                <Card className="mb-3 border-0 bg-light">
                  <Card.Body className="py-2">
                    <small className="text-muted">
                      <i className="fa fa-lightbulb text-warning me-2"></i>
                      <strong>Pro Tip:</strong> Enter a percentage number (0-100). Example: 89, 93, 87
                    </small>
                  </Card.Body>
                </Card>

                <Row>
                  <Col md={12} className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fa fa-percent me-2 text-primary"></i>
                      Percentage Number <span className="text-danger">*</span>
                    </label>
                    <CustomInput
                      name="number"
                      type="number"
                      placeholder="e.g., 89"
                      className="form-control"
                      min="0"
                      max="100"
                      isRequired
                    />
                    <small className="text-muted">Enter a number between 0-100 (% sign will be added automatically)</small>
                  </Col>

                  <Col md={12} className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fa fa-align-left me-2 text-primary"></i>
                      Description Text <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="text"
                      placeholder="e.g., Agree that our services save their time compared to traditional pharmacies"
                      className="form-control"
                      rows="3"
                      value={formik.values.text}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.text && formik.errors.text && (
                      <div className="text-danger small mt-1">
                        {formik.errors.text}
                      </div>
                    )}
                    <small className="text-muted">Brief description of what this percentage represents</small>
                  </Col>

                  <Col md={12} className="mb-3">
                    <label className="form-label fw-bold d-block">
                      <i className="fa fa-toggle-on me-2 text-primary"></i>
                      Display Status
                    </label>
                    <ReactSwitch
                      uncheckedIcon={false}
                      checkedIcon={false}
                      onChange={(checked) => formik.setFieldValue('status', checked)}
                      checked={formik.values.status}
                      height={24}
                      width={48}
                      onColor="#28a745"
                      offColor="#dc3545"
                    />
                    <small className="text-muted d-block mt-2">
                      {formik.values.status ? 'This stat will be visible on the website' : 'This stat will be hidden from the website'}
                    </small>
                  </Col>
                </Row>

                {/* Preview Card */}
                <Card className="mt-4 border" style={{ borderLeft: '4px solid #0d6efd' }}>
                  <Card.Header className="bg-light">
                    <strong><i className="fa fa-eye me-2"></i>Preview</strong>
                  </Card.Header>
                  <Card.Body className="text-center py-4">
                    <h1 className="display-4 fw-bold text-primary mb-2">
                      {formik.values.number || '0'}%
                    </h1>
                    <p className="text-muted mb-0">
                      {formik.values.text || 'Description text will appear here'}
                    </p>
                  </Card.Body>
                </Card>
              </Modal.Body>

              <Modal.Footer className="bg-light">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingItem(null);
                  }}
                >
                  <i className="fa fa-times me-2"></i>Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>Saving...
                    </>
                  ) : (
                    <>
                      <i className={`fa fa-${editingItem ? 'save' : 'plus'} me-2`}></i>
                      {editingItem ? 'Update' : 'Add'} Stat
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>

      <DeleteConfirmationModal
        name="Analytics Stat"
        show={showDeleteModal}
        handleToggle={() => setShowDeleteModal(false)}
        handleDelete={confirmDelete}
      />

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName="Analytics"
      />
    </div>
  );
};

export default AnalyticsManager;
